import { useState, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  extractThreadRelationalFacts as extractThreadFacts,
  synthesizePersonRelational as aggregatePersonProfile,
  generateNextBestAction as generatePAMSSACAction,
  type ThreadRelationalFacts as ThreadFacts,
  type PersonRelationalSynthesis as PersonProfile,
  type NextBestAction,
} from '@/services/relationalIntelligenceLLM';
import { calculatePAMSSACScoreV2 as calculatePAMSSACScore, type PAMSSACScore } from '@/services/pamssacScoringEngineV2';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Play,
  RefreshCw,
  FileText,
  User,
  Target,
  Zap,
  Brain,
  TrendingUp,
} from 'lucide-react';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false,
  auth: { mode: 'headless' }
});

interface TestResult {
  testName: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  duration?: number;
  error?: string;
  data?: any;
  structuredOutputValid?: boolean;
  aiCallDetails?: {
    model?: string;
    tokensUsed?: number;
    latency?: number;
  };
}

interface TestSuiteResults {
  threadExtraction: TestResult;
  personProfile: TestResult;
  pamssacScoring: TestResult;
  nextBestAction: TestResult;
  batchProcessing: TestResult;
}

export default function AICallTestSuite() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TestSuiteResults>({
    threadExtraction: { testName: 'Thread-to-Facts Extraction', status: 'pending' },
    personProfile: { testName: 'Person Profile Aggregation', status: 'pending' },
    pamssacScoring: { testName: 'PAMSSAC Scoring Engine', status: 'pending' },
    nextBestAction: { testName: 'Next Best Action Policy', status: 'pending' },
    batchProcessing: { testName: 'Batch Processing & Throttling', status: 'pending' },
  });
  const [selectedTest, setSelectedTest] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      if (state.user) {
        setUser(state.user);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateResult = (key: keyof TestSuiteResults, updates: Partial<TestResult>) => {
    setResults((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...updates },
    }));
  };

  /**
   * Test 1: Thread-to-Facts Extraction (LLM-based)
   */
  const testThreadExtraction = async () => {
    updateResult('threadExtraction', { status: 'running' });
    const startTime = Date.now();

    try {
      // Get a real thread from the database
      const threads = await blink.db.emailThreads.list({
        where: { userId: user.id },
        limit: 5,
      });

      if (threads.length === 0) {
        throw new Error('No email threads found');
      }

      // Extract facts from the first thread
      const thread = threads[0];
      // Fetch messages for the thread
      let messages: any[] = [];
      try {
        const me = (blink.db as any).messagesEnhanced;
        if (me && typeof me.list === 'function') {
          messages = await me.list({ where: { threadId: thread.id, userId: user.id }, limit: 200 });
        }
      } catch (e) {
        console.warn('messagesEnhanced not available, trying fallback messages table', e);
      }

      if (messages.length === 0) {
        // Fallback minimal message shape
        messages = [ { id: thread.id, subject: thread.subject || '', bodyText: thread.snippet || '', senderEmail: thread.participants?.[0] || '' } ];
      }

      const facts: ThreadFacts = await extractThreadFacts(thread.id, messages);

      const duration = Date.now() - startTime;

      // Validate structured output
      const isValid = validateThreadFacts(facts);

      updateResult('threadExtraction', {
        status: isValid ? 'passed' : 'failed',
        duration,
        data: facts,
        structuredOutputValid: isValid,
        error: isValid ? undefined : 'Structured output validation failed',
        aiCallDetails: {
          model: 'gpt-4.1',
          latency: duration,
        },
      });

      if (isValid) {
        toast.success('Thread extraction passed! ✓');
      } else {
        toast.error('Thread extraction failed - invalid structure');
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      updateResult('threadExtraction', {
        status: 'failed',
        duration,
        error: error.message,
      });
      toast.error(`Thread extraction failed: ${error.message}`);
    }
  };

  /**
   * Test 2: Person Profile Aggregation
   */
  const testPersonProfile = async () => {
    updateResult('personProfile', { status: 'running' });
    const startTime = Date.now();

    try {
      // Get a real contact from the database
      const contacts = await blink.db.contacts.list({
        where: { userId: user.id },
        limit: 5,
      });

      if (contacts.length === 0) {
        throw new Error('No contacts found');
      }

      // Aggregate person profile
      const contact = contacts[0];
      // Try to fetch existing thread facts for this person
      let threadFacts: any[] = [];
      try {
        threadFacts = await blink.db.threadRelationalFacts.list({ where: { userId: user.id, personId: contact.id }, limit: 50 });
      } catch (e) {
        console.warn('threadRelationalFacts table not available or query failed', e);
      }

      if (!Array.isArray(threadFacts) || threadFacts.length === 0) {
        throw new Error('No thread facts available for this person');
      }

      const profile: PersonProfile = await aggregatePersonProfile(contact.id, threadFacts as ThreadFacts[]);

      const duration = Date.now() - startTime;

      // Validate structured output
      const isValid = validatePersonProfile(profile);

      updateResult('personProfile', {
        status: isValid ? 'passed' : 'failed',
        duration,
        data: profile,
        structuredOutputValid: isValid,
        error: isValid ? undefined : 'Structured output validation failed',
        aiCallDetails: {
          model: 'gpt-4.1',
          latency: duration,
        },
      });

      if (isValid) {
        toast.success('Person profile aggregation passed! ✓');
      } else {
        toast.error('Person profile failed - invalid structure');
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      updateResult('personProfile', {
        status: 'failed',
        duration,
        error: error.message,
      });
      toast.error(`Person profile failed: ${error.message}`);
    }
  };

  /**
   * Test 3: PAMSSAC Scoring Engine
   */
  const testPAMSSACScoring = async () => {
    updateResult('pamssacScoring', { status: 'running' });
    const startTime = Date.now();

    try {
      // Get a real contact
      const contacts = await blink.db.contacts.list({
        where: { userId: user.id },
        limit: 5,
      });

      if (contacts.length === 0) {
        throw new Error('No contacts found');
      }

      // Calculate PAMSSAC score
      const contact = contacts[0];
      const pamssac: PAMSSACScore = await calculatePAMSSACScore(user.id, contact.id);

      const duration = Date.now() - startTime;

      // Validate structured output
      const isValid = validatePAMSSACScore(pamssac);

      updateResult('pamssacScoring', {
        status: isValid ? 'passed' : 'failed',
        duration,
        data: pamssac,
        structuredOutputValid: isValid,
        error: isValid ? undefined : 'Structured output validation failed',
        aiCallDetails: {
          model: 'gpt-4.1-mini',
          latency: duration,
        },
      });

      if (isValid) {
        toast.success('PAMSSAC scoring passed! ✓');
      } else {
        toast.error('PAMSSAC scoring failed - invalid structure');
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      updateResult('pamssacScoring', {
        status: 'failed',
        duration,
        error: error.message,
      });
      toast.error(`PAMSSAC scoring failed: ${error.message}`);
    }
  };

  /**
   * Test 4: Next Best Action Policy
   */
  const testNextBestAction = async () => {
    updateResult('nextBestAction', { status: 'running' });
    const startTime = Date.now();

    try {
      // Get a real contact with PAMSSAC score
      const contacts = await blink.db.contacts.list({
        where: { userId: user.id },
        limit: 5,
      });

      if (contacts.length === 0) {
        throw new Error('No contacts found');
      }

      const contact = contacts[0];
      const pamssac = await calculatePAMSSACScore(user.id, contact.id);

      // Generate next best action
      const contactCard = {
        name: contact.name,
        email: contact.email,
        openLoops: [],
      };
      // Minimal synthesized profile for NBA generation
      const profileForNBA: PersonProfile = {
        personId: contact.id,
        summary: contact.name || contact.email,
        goalsTop5: [],
        needsTop5: [],
        wantsTop5: [],
        canHelpWithTop3: [],
        communicationStyle: 'direct',
        preferenceCues: [],
        emotionalSignature: { avgSentiment: 0, stability: 'steady', empathyRate: 0, enthusiasmRate: 0 },
        reciprocityLedger: { gives: 0, gets: 0, introsMade: 0, introsReceived: 0 },
        lastContacted: new Date().toISOString(),
        evidenceThreads: []
      } as PersonProfile;

      // Build minimal RC scores from PAMSSAC for NBA generation
      const rcScores: any = {
        SAI: pamssac.total,
        RH: pamssac.total,
        EC: pamssac.total,
        CIg: pamssac.total,
        TD: 0,
        PF: 0,
        NL: 0,
        RC: 0,
        ER: pamssac.total,
      };

      const action: NextBestAction = await generatePAMSSACAction(contact.name || contact.email, profileForNBA, rcScores, 'customers');

      const duration = Date.now() - startTime;

      // Validate structured output
      const isValid = validateNextBestAction(action);

      updateResult('nextBestAction', {
        status: isValid ? 'passed' : 'failed',
        duration,
        data: action,
        structuredOutputValid: isValid,
        error: isValid ? undefined : 'Structured output validation failed',
        aiCallDetails: {
          model: 'Deterministic (rule-based)',
          latency: duration,
        },
      });

      if (isValid) {
        toast.success('Next best action passed! ✓');
      } else {
        toast.error('Next best action failed - invalid structure');
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      updateResult('nextBestAction', {
        status: 'failed',
        duration,
        error: error.message,
      });
      toast.error(`Next best action failed: ${error.message}`);
    }
  };

  /**
   * Test 5: Batch Processing & Throttling
   */
  const testBatchProcessing = async () => {
    updateResult('batchProcessing', { status: 'running' });
    const startTime = Date.now();

    try {
      // Get multiple contacts
      const contacts = await blink.db.contacts.list({
        where: { userId: user.id },
        limit: 10,
      });

      if (contacts.length === 0) {
        throw new Error('No contacts found');
      }

      // Process in batches of 3 with throttling
      const batchSize = 3;
      const results = [];

      for (let i = 0; i < contacts.length; i += batchSize) {
        const batch = contacts.slice(i, i + batchSize);

        // Process batch in parallel
        const batchResults = await Promise.allSettled(
          batch.map((contact) => calculatePAMSSACScore(user.id, contact.id))
        );

        results.push(...batchResults);

        // Throttle between batches
        if (i + batchSize < contacts.length) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      const duration = Date.now() - startTime;

      // Check success rate
      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      const isValid = successful > 0 && failed < successful;

      updateResult('batchProcessing', {
        status: isValid ? 'passed' : 'failed',
        duration,
        data: { successful, failed, total: results.length },
        structuredOutputValid: isValid,
        error: isValid ? undefined : 'Too many batch failures',
        aiCallDetails: {
          model: 'Batch processing',
          latency: duration / results.length,
        },
      });

      if (isValid) {
        toast.success(
          `Batch processing passed! ${successful}/${results.length} successful ✓`
        );
      } else {
        toast.error(`Batch processing failed: ${failed}/${results.length} errors`);
      }
    } catch (error: any) {
      const duration = Date.now() - startTime;
      updateResult('batchProcessing', {
        status: 'failed',
        duration,
        error: error.message,
      });
      toast.error(`Batch processing failed: ${error.message}`);
    }
  };

  /**
   * Run all tests
   */
  const runAllTests = async () => {
    if (!user) {
      toast.error('Please login first');
      return;
    }

    setLoading(true);
    toast.info('Running all AI tests...');

    try {
      await testThreadExtraction();
      await new Promise((resolve) => setTimeout(resolve, 500));

      await testPersonProfile();
      await new Promise((resolve) => setTimeout(resolve, 500));

      await testPAMSSACScoring();
      await new Promise((resolve) => setTimeout(resolve, 500));

      await testNextBestAction();
      await new Promise((resolve) => setTimeout(resolve, 500));

      await testBatchProcessing();

      // Check overall results
      const allResults = Object.values(results);
      const passed = allResults.filter((r) => r.status === 'passed').length;
      const total = allResults.length;

      if (passed === total) {
        toast.success(`All tests passed! ${passed}/${total} ✓`);
      } else {
        toast.warning(`${passed}/${total} tests passed`);
      }
    } catch (error: any) {
      toast.error(`Test suite failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'passed':
        return (
          <Badge variant="default" className="bg-green-500">
            Passed
          </Badge>
        );
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'running':
        return <Badge variant="secondary">Running...</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto" />
          <p className="text-muted-foreground">Please login to run AI tests</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Brain className="w-8 h-8 text-primary" />
            AI Call Test Suite
          </h1>
          <p className="text-muted-foreground mt-2">
            Live testing of all AI-powered structured outputs with real data
          </p>
        </div>
        <Button onClick={runAllTests} disabled={loading} size="lg" className="gap-2">
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Running Tests...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run All Tests
            </>
          )}
        </Button>
      </div>

      {/* Test Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(results).map(([key, result]) => (
          <Card
            key={key}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedTest(key)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                {getStatusIcon(result.status)}
                {getStatusBadge(result.status)}
              </div>
              <p className="text-sm font-medium">{result.testName}</p>
              {result.duration && (
                <p className="text-xs text-muted-foreground mt-1">
                  {result.duration}ms
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Individual Test Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Thread-to-Facts Extraction
            </CardTitle>
            <CardDescription>
              Test LLM-based extraction of structured facts from email threads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={testThreadExtraction}
              disabled={loading}
              className="w-full"
              variant="outline"
            >
              <Play className="w-4 h-4 mr-2" />
              Test Thread Extraction
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-green-500" />
              Person Profile Aggregation
            </CardTitle>
            <CardDescription>
              Test aggregation of person profiles across multiple threads
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={testPersonProfile}
              disabled={loading}
              className="w-full"
              variant="outline"
            >
              <Play className="w-4 h-4 mr-2" />
              Test Person Profile
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              PAMSSAC Scoring Engine
            </CardTitle>
            <CardDescription>
              Test full 7-dimension PAMSSAC scoring with deterministic formulas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={testPAMSSACScoring}
              disabled={loading}
              className="w-full"
              variant="outline"
            >
              <Play className="w-4 h-4 mr-2" />
              Test PAMSSAC Scoring
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Next Best Action Policy
            </CardTitle>
            <CardDescription>
              Test rule-based policy engine for generating actionable steps
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={testNextBestAction}
              disabled={loading}
              className="w-full"
              variant="outline"
            >
              <Play className="w-4 h-4 mr-2" />
              Test Next Action
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Results */}
      {selectedTest && (
        <Card>
          <CardHeader>
            <CardTitle>Test Details: {results[selectedTest as keyof TestSuiteResults].testName}</CardTitle>
            <CardDescription>
              View structured output and validation results
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <p className="text-sm font-medium mb-2">Status</p>
                  {getStatusBadge(results[selectedTest as keyof TestSuiteResults].status)}
                </div>

                {/* Duration */}
                {results[selectedTest as keyof TestSuiteResults].duration && (
                  <div>
                    <p className="text-sm font-medium mb-2">Duration</p>
                    <p className="text-sm text-muted-foreground">
                      {results[selectedTest as keyof TestSuiteResults].duration}ms
                    </p>
                  </div>
                )}

                {/* Error */}
                {results[selectedTest as keyof TestSuiteResults].error && (
                  <div>
                    <p className="text-sm font-medium mb-2 text-red-500">Error</p>
                    <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950 p-3 rounded">
                      {results[selectedTest as keyof TestSuiteResults].error}
                    </p>
                  </div>
                )}

                {/* Data */}
                {results[selectedTest as keyof TestSuiteResults].data && (
                  <div>
                    <p className="text-sm font-medium mb-2">Structured Output</p>
                    <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-[500px]">
                      {JSON.stringify(
                        results[selectedTest as keyof TestSuiteResults].data,
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}

                {/* AI Call Details */}
                {results[selectedTest as keyof TestSuiteResults].aiCallDetails && (
                  <div>
                    <p className="text-sm font-medium mb-2">AI Call Details</p>
                    <div className="space-y-1 text-sm">
                      <p>
                        Model:{' '}
                        {results[selectedTest as keyof TestSuiteResults].aiCallDetails?.model}
                      </p>
                      <p>
                        Latency:{' '}
                        {results[selectedTest as keyof TestSuiteResults].aiCallDetails?.latency}ms
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Validation Functions
// ============================================================================

function validateThreadFacts(facts: ThreadFacts): boolean {
  return !!(
    facts.threadId &&
    facts.summary &&
    Array.isArray(facts.theirPriorities) &&
    Array.isArray(facts.canHelpWith) &&
    Array.isArray(facts.interests) &&
    facts.communicationStyle &&
    typeof facts.sentiment === 'number' &&
    facts.intent &&
    Array.isArray(facts.openCommitments) &&
    facts.pamssacSignals &&
    facts.pamssacSignals.urgency &&
    Array.isArray(facts.evidence)
  );
}

function validatePersonProfile(profile: PersonProfile): boolean {
  return !!(
    profile.personId &&
    profile.emailPrimary &&
    profile.summary &&
    Array.isArray(profile.prioritiesTop3) &&
    Array.isArray(profile.canHelpWithTop3) &&
    Array.isArray(profile.interestsTop5) &&
    profile.communicationStyle &&
    profile.lastContacted &&
    Array.isArray(profile.evidenceThreads)
  );
}

function validatePAMSSACScore(pamssac: PAMSSACScore): boolean {
  return !!(
    typeof pamssac.P === 'number' &&
    typeof pamssac.A1 === 'number' &&
    typeof pamssac.M === 'number' &&
    typeof pamssac.S1 === 'number' &&
    typeof pamssac.S2 === 'number' &&
    typeof pamssac.A2 === 'number' &&
    typeof pamssac.C === 'number' &&
    typeof pamssac.total === 'number' &&
    pamssac.tier &&
    Array.isArray(pamssac.weakestTwo) &&
    pamssac.evidence &&
    pamssac.evidence.P &&
    pamssac.evidence.A1 &&
    pamssac.evidence.M &&
    pamssac.evidence.S1 &&
    pamssac.evidence.S2 &&
    pamssac.evidence.A2 &&
    pamssac.evidence.C
  );
}

function validateNextBestAction(action: NextBestAction): boolean {
  return !!(
    action.action &&
    Array.isArray(action.why) &&
    action.deadline &&
    Array.isArray(action.evidence) &&
    action.messageText
  );
}
